import React, { memo } from 'react';
import { Card, CardContent, CardProps, Typography, Box, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import FallbackImage from '../FallbackImage';

interface Article {
  id: string | number;
  image: string;
  title?: string;
  description?: string;
  date?: string;
}

interface ArticleCardProps extends CardProps {
  article: Article;
  onClick: () => void;
}

export const buildArticleHref = (article: Article, locale, t): string => {
  let href = `/article/${article.id}/${article.title}`;
  if (locale?.toLowerCase() !== 'th') href = `/${locale}${href}`;
  return href;
};

const ArticleCard: React.FC<ArticleCardProps> = memo(({ article, onClick, sx, ...props }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Card
      onClick={onClick}
      sx={{
        borderRadius: theme.shape.borderRadius,
        mx: 0.7,
        boxShadow: 'none',
        cursor: 'pointer',
        transition: theme.transitions.create('box-shadow'),
        '&:hover': {
          boxShadow: '0px 2px 10px rgba(0,0,0,0.1)',
        },
        height: '100%',
        ...sx,
      }}
      {...props}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: 200,
          borderTopLeftRadius: theme.shape.borderRadius,
          borderTopRightRadius: theme.shape.borderRadius,
          overflow: 'hidden',
        }}
      >
        <FallbackImage
          src={article.image}
          alt={article.title || t('not-specified')}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 360px"
          loading="lazy"
        />
      </Box>

      <CardContent sx={{ p: theme.spacing(2) }}>
        <Typography
          variant="h4"
          fontWeight={500}
          gutterBottom
          sx={{
            fontSize: '1rem',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {article.title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'grey.700',
            fontSize: '0.75rem',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 7,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            mb: theme.spacing(1),
          }}
        >
          {article.description}
        </Typography>

        {article.date && (
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
            {article.date}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
});

ArticleCard.displayName = 'ArticleCard';

export default ArticleCard;
